// Growth › Import leads: upload, map columns, preview, import.
//
// The file is read in the browser and never uploaded as a file: the portal
// sends mapped rows, and the server decides what happens to each one, the same
// way for the preview (dry_run) and the import. Nothing here decides a
// duplicate. A file with the RecLeague list's columns starts mapped.

import { useState, type ChangeEvent } from 'react'
import { Link } from 'react-router-dom'
import { api } from '@/api'
import { errorMessage } from '@/api/client'
import type { LeadImportReport, LeadImportResult, LeadImportRow } from '@/api/types'
import { LEAD_TYPES } from '@/api/types'
import { PageHeader } from '@/components/shell/PageHeader'
import {
  Button,
  ButtonLink,
  Card,
  CardHeader,
  DataTable,
  FilterChips,
  FormField,
  InlineAlert,
  SelectInput,
  StatTile,
  TextInput,
  UploadIcon,
  type Column,
} from '@/components/ui'
import { parseCsv, type Csv } from '@/lib/csv'
import { cn } from '@/lib/format'
import { LEAD_TYPE_LABELS } from '@/lib/growth'
import { useDocumentTitle } from '@/lib/hooks'
import { buildRows, IGNORE, LEAD_FIELDS, setMapping, suggestImport, type ImportDefaults, type LeadField, type Mapping } from '@/lib/leadImport'
import { invalidateQueries } from '@/lib/query'

const MAX_FILE_BYTES = 8 * 1024 * 1024
const MAX_ROWS = 5000
const SHOWN_ROWS = 200

const STEPS = ['Upload', 'Map columns', 'Preview', 'Done'] as const
type Step = 0 | 1 | 2 | 3

const FIELD_OPTIONS = [{ value: IGNORE, label: 'Leave out' }, ...LEAD_FIELDS.map((f) => ({ value: f.value, label: f.label }))]

const REASON_LABELS: Record<string, string> = {
  missing_name: 'No name',
  invalid_email: 'Bad email',
  invalid_website: 'Bad website',
  invalid_value: 'Bad value',
  no_contact: 'No way to reach them',
  suppressed: 'On the do-not-contact list',
  do_not_contact_applied: 'Existing lead moved to do not contact',
  duplicate_email: 'Email already a lead',
  duplicate_website: 'Website already a lead',
  duplicate_source: 'Source URL already a lead',
  duplicate_in_file: 'Repeated in this file',
}

function Steps({ step }: { step: Step }) {
  return (
    <ol aria-label="Import steps" className="flex flex-wrap gap-x-4 gap-y-1 text-xs font-semibold">
      {STEPS.map((label, i) => (
        <li key={label} aria-current={i === step ? 'step' : undefined} className={cn(i === step ? 'text-ink' : 'text-subtle')}>
          {i + 1}. {label}
        </li>
      ))}
    </ol>
  )
}

const RESULT_COLUMNS: Column<LeadImportResult>[] = [
  { id: 'row', header: 'Row', cell: (r) => <span className="font-mono text-xs tabular">{r.row}</span>, className: 'w-14' },
  { id: 'name', header: 'Name', primary: true, cell: (r) => <span className="break-words">{r.name ?? 'No name'}</span>, className: 'min-w-[12rem]' },
  { id: 'why', header: 'Outcome', cell: (r) => <span className="text-muted">{r.action === 'add' ? 'Add' : r.message}</span> },
]

function Report({ report }: { report: LeadImportReport }) {
  const { summary, results } = report
  const [view, setView] = useState<'skip' | 'add'>(summary.skipped > 0 ? 'skip' : 'add')
  const shown = results.filter((r) => r.action === view)
  const reasons = Object.entries(summary.reasons).sort((a, b) => b[1] - a[1])
  return (
    <div className="grid gap-4">
      <div className="grid grid-cols-3 gap-3">
        <StatTile label="Rows" value={summary.total} />
        <StatTile label={report.dry_run ? 'Would add' : 'Added'} value={summary.added} tone="accent" />
        <StatTile label={report.dry_run ? 'Would skip' : 'Skipped'} value={summary.skipped} tone={summary.skipped ? 'warn' : 'default'} />
      </div>
      {report.verification?.run_id ? (
        <InlineAlert title="Verifying what was added">
          Each new lead is being checked against its live site, in the background. {report.verification.note}{' '}
          <Link to={`/growth/finder/runs/${report.verification.run_id}`} className="font-semibold underline">
            See the log
          </Link>
        </InlineAlert>
      ) : report.verification?.note ? (
        <InlineAlert tone="warn" title="Not verified yet">
          {report.verification.note}
        </InlineAlert>
      ) : null}
      {reasons.length > 0 ? (
        <Card>
          <CardHeader title="Why rows are skipped" />
          <ul aria-label="Skip reasons" className="grid gap-1 text-[13px]">
            {reasons.map(([reason, n]) => (
              <li key={reason} className="flex items-center justify-between gap-3">
                <span className="text-ink">{REASON_LABELS[reason] ?? reason}</span>
                <span className="font-mono text-xs text-muted tabular">{n}</span>
              </li>
            ))}
          </ul>
        </Card>
      ) : null}
      <Card flush className="overflow-hidden">
        <div className="border-b border-line px-4 py-3">
          <FilterChips
            label="Show rows"
            options={[
              { value: 'skip' as const, label: 'Skipped', count: summary.skipped },
              { value: 'add' as const, label: report.dry_run ? 'To add' : 'Added', count: summary.added },
            ]}
            value={view}
            onChange={setView}
          />
        </div>
        {shown.length ? (
          <DataTable caption="Import rows" rows={shown.slice(0, SHOWN_ROWS)} columns={RESULT_COLUMNS} rowKey={(r) => r.row} />
        ) : (
          <p className="px-4 py-6 text-center text-sm text-muted">No rows here.</p>
        )}
        {shown.length > SHOWN_ROWS ? <p className="border-t border-line px-4 py-2 text-xs text-muted">Showing the first {SHOWN_ROWS} of {shown.length}.</p> : null}
      </Card>
    </div>
  )
}

export default function LeadImportPage() {
  useDocumentTitle('Import leads')
  const [step, setStep] = useState<Step>(0)
  const [fileName, setFileName] = useState('')
  const [csv, setCsv] = useState<Csv | null>(null)
  const [mapping, setMap] = useState<Mapping>([])
  const [defaults, setDefaults] = useState<ImportDefaults | null>(null)
  const [recLeague, setRecLeague] = useState(false)
  const [skipWithoutContact, setSkipWithoutContact] = useState(false)
  const [report, setReport] = useState<LeadImportReport | null>(null)
  const [problem, setProblem] = useState<string | null>(null)
  const [pending, setPending] = useState(false)

  const start = () => {
    setStep(0)
    setCsv(null)
    setReport(null)
    setProblem(null)
    setFileName('')
  }

  const onFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    setProblem(null)
    if (file.size > MAX_FILE_BYTES) return setProblem('That file is over 8 MB. Split it and import the parts.')
    let parsed: Csv
    try {
      parsed = parseCsv(await file.text())
    } catch (err) {
      return setProblem(errorMessage(err, "Couldn't read that file."))
    }
    if (parsed.headers.length === 0 || parsed.rows.length === 0) return setProblem('That file has no rows under its header line.')
    if (parsed.rows.length > MAX_ROWS) return setProblem(`That file has ${parsed.rows.length} rows. Import at most ${MAX_ROWS} at a time.`)
    const suggested = suggestImport(parsed.headers)
    setFileName(file.name)
    setCsv(parsed)
    setMap(suggested.mapping)
    setDefaults(suggested.defaults)
    setRecLeague(suggested.recLeague)
    setStep(1)
  }

  const rows = (): LeadImportRow[] => (csv && defaults ? buildRows(csv, mapping, defaults) : [])

  const run = async (dryRun: boolean) => {
    setPending(true)
    setProblem(null)
    try {
      const result = await api.growth.leads.import({ rows: rows(), skip_without_contact: skipWithoutContact, dry_run: dryRun, verify: !dryRun })
      setReport(result)
      setStep(dryRun ? 2 : 3)
      if (!dryRun) invalidateQueries('growth')
    } catch (err) {
      setProblem(errorMessage(err))
    } finally {
      setPending(false)
    }
  }

  const nameMapped = mapping.includes('name')
  const setDefault = (key: keyof ImportDefaults) => (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setDefaults((d) => (d ? { ...d, [key]: e.target.value } : d))

  return (
    <div className="grid grid-cols-1 gap-5">
      <PageHeader
        crumbs={[{ label: 'Leads', to: '/growth/leads' }, { label: 'Import' }]}
        title="Import leads"
        description="Rows that match an existing lead by email, website or source URL are skipped. An import adds; it never changes a lead that's already here."
      />
      <Steps step={step} />
      {problem ? <InlineAlert tone="bad">{problem}</InlineAlert> : null}

      {step === 0 ? (
        <Card>
          <CardHeader title="Choose a CSV" description="The first line must be the column names. Public organisation contacts only." />
          <label className="flex cursor-pointer flex-col items-center gap-2 rounded-md border border-dashed border-line-strong px-4 py-10 text-center hover:border-subtle">
            <UploadIcon size={22} className="text-subtle" />
            <span className="text-sm font-semibold text-ink">Choose a file</span>
            <span className="text-xs text-muted">Up to {MAX_ROWS.toLocaleString()} rows</span>
            <input type="file" accept=".csv,text/csv" aria-label="CSV file" className="sr-only" onChange={(e) => void onFile(e)} />
          </label>
        </Card>
      ) : null}

      {step === 1 && csv && defaults ? (
        <>
          {recLeague ? (
            <InlineAlert title="This looks like the RecLeague list">
              The columns are mapped and the source and platform are filled in. Social links, the contact page, the team count and the archived flag go into the notes.
            </InlineAlert>
          ) : null}
          <Card>
            <CardHeader title={`${fileName}: ${csv.rows.length} ${csv.rows.length === 1 ? 'row' : 'rows'}`} description="Pick a lead field for each column. Several columns can go into Notes." />
            <ul aria-label="Column mapping" className="grid gap-3">
              {csv.headers.map((header, i) => (
                <li key={`${header}-${i}`} className="grid gap-1.5 sm:grid-cols-[1fr_12rem] sm:items-center sm:gap-4">
                  <div className="min-w-0">
                    <p className="truncate font-mono text-[13px] font-semibold text-ink">{header || `Column ${i + 1}`}</p>
                    <p className="truncate text-xs text-muted">{csv.rows.find((r) => r[i]?.trim())?.[i] ?? 'Empty'}</p>
                  </div>
                  <SelectInput
                    aria-label={`Field for ${header || `column ${i + 1}`}`}
                    value={mapping[i] ?? IGNORE}
                    options={FIELD_OPTIONS}
                    onChange={(e) => setMap((m) => setMapping(m, i, e.target.value as LeadField | typeof IGNORE))}
                  />
                </li>
              ))}
            </ul>
          </Card>
          <Card>
            <CardHeader title="Defaults" description="Used on any row that doesn't say otherwise." />
            <div className="grid gap-4 sm:grid-cols-3">
              <FormField label="Source">{({ id }) => <TextInput id={id} value={defaults.source} maxLength={64} onChange={setDefault('source')} />}</FormField>
              <FormField label="Current platform">{({ id }) => <TextInput id={id} value={defaults.platform} maxLength={64} onChange={setDefault('platform')} />}</FormField>
              <FormField label="Sport">{({ id }) => <TextInput id={id} value={defaults.sport} maxLength={64} onChange={setDefault('sport')} />}</FormField>
              <FormField label="Type">
                {({ id }) => <SelectInput id={id} value={defaults.type} options={LEAD_TYPES.map((t) => ({ value: t, label: LEAD_TYPE_LABELS[t] }))} onChange={setDefault('type')} />}
              </FormField>
              <FormField label="Country">{({ id }) => <TextInput id={id} value={defaults.country} maxLength={64} onChange={setDefault('country')} />}</FormField>
            </div>
            <label className="mt-4 flex items-start gap-2.5 text-[13px] text-ink">
              <input type="checkbox" checked={skipWithoutContact} onChange={(e) => setSkipWithoutContact(e.target.checked)} className="mt-0.5 h-4 w-4 accent-accent-solid" />
              <span>
                Skip rows with no email, phone or website
                <span className="block text-xs text-muted">Nobody could reach them, so they'd only be a name in the list.</span>
              </span>
            </label>
          </Card>
          {!nameMapped ? <InlineAlert tone="warn">Pick the column that holds the name. Every lead needs one.</InlineAlert> : null}
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button onClick={start} disabled={pending}>
              Start over
            </Button>
            <Button variant="primary" disabled={!nameMapped} loading={pending} onClick={() => void run(true)}>
              Preview
            </Button>
          </div>
        </>
      ) : null}

      {step === 2 && report ? (
        <>
          <Report report={report} />
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button onClick={() => setStep(1)} disabled={pending}>
              Back to mapping
            </Button>
            <Button variant="primary" disabled={report.summary.added === 0} loading={pending} onClick={() => void run(false)}>
              Import {report.summary.added} {report.summary.added === 1 ? 'lead' : 'leads'}
            </Button>
          </div>
        </>
      ) : null}

      {step === 3 && report ? (
        <>
          <InlineAlert tone="good" title={`${report.summary.added} added, ${report.summary.skipped} skipped`}>
            The import is in the audit log.
          </InlineAlert>
          <Report report={report} />
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button onClick={start}>Import another file</Button>
            <ButtonLink to="/growth/leads" variant="primary">
              View leads
            </ButtonLink>
          </div>
        </>
      ) : null}
    </div>
  )
}
